import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SBINModal from "../components/modals/SBINModal";
import ModalCloseX from "../components/common/ModalCloseX";
import Tooltip from "../components/common/Tooltip";
import InfoTooltip from "../components/common/InfoTooltip";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import { useAuth } from "../context/AuthContext";
import "./ReportsSectionCompact.css";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";
import {
  createTotalSheetJob,
  downloadBlob,
  downloadReportJobFile,
  exportTotalSheetNow,
  getReportJob,
  getTotalSheetSummary,
} from "../api/reports";
import { getSalonTimezone } from "../utils/salonTimezone";
import { TOTAL_SHEET_TOOLTIPS } from "../constants/reportTooltips";
import { REPORT_HELP } from "../constants/reportHelpContent";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";

const TOOLTIP_MAX_WIDTH = 240;
const EMPLOYEE_SORT_COLUMNS = {
  name: { label: "Name", type: "text" },
  tickets: { label: "Tickets", type: "number" },
  services: { label: "Services", type: "number" },
  tips: { label: "Tips", type: "number" },
};
const SECTION_TOOLTIP_KEYS = {
  "Net Sales": "net_sales",
  Refunds: "refunds",
  Discounts: "discounts",
  Taxes: "taxes",
  Payments: "payments",
};

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      const parts = formatter.formatToParts(date);
      const lookup = { year: "", month: "", day: "" };
      for (const part of parts) {
        if (Object.prototype.hasOwnProperty.call(lookup, part.type)) {
          lookup[part.type] = part.value;
        }
      }
      if (lookup.year && lookup.month && lookup.day) {
        return `${lookup.year}-${lookup.month}-${lookup.day}`;
      }
    } catch (err) {
      // Fall back to local time when timezone is invalid or unsupported.
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateFromParts = (year, month, day) =>
  `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

const addDaysToIsoDate = (dateStr, deltaDays) => {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  if (parts.length !== 3) return "";
  const [year, month, day] = parts.map((value) => Number(value));
  if (![year, month, day].every((value) => Number.isFinite(value))) return "";
  const base = new Date(Date.UTC(year, month - 1, day));
  base.setUTCDate(base.getUTCDate() + deltaDays);
  return formatDateFromParts(
    base.getUTCFullYear(),
    base.getUTCMonth() + 1,
    base.getUTCDate()
  );
};

const getWeekdayIndex = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split("-");
  if (parts.length !== 3) return null;
  const [year, month, day] = parts.map((value) => Number(value));
  if (![year, month, day].every((value) => Number.isFinite(value))) return null;
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
};

const isInvalidDateRange = (startValue, endValue) => {
  if (!startValue || !endValue) return false;
  return endValue < startValue;
};

const formatMoney = (value) => {
  const number = Number(value ?? 0);
  if (Number.isNaN(number)) return "$0.00";
  return `$${number.toFixed(2)}`;
};

const formatPercent = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return `${(number * 100).toFixed(1)}%`;
};

const toMixKey = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (!normalized) return "";
  const chars = [];
  for (const ch of normalized) {
    chars.push(/[a-z0-9]/.test(ch) ? ch : "_");
  }
  let key = chars.join("");
  while (key.includes("__")) {
    key = key.replaceAll("__", "_");
  }
  return key.replace(/^_+|_+$/g, "");
};

const buildDefaultName = (start, end) => {
  if (!start || !end) return "Total Sheet";
  const cleanStart = start.replaceAll("-", "");
  const cleanEnd = end.replaceAll("-", "");
  if (start === end) {
    return `Total Sheet ${cleanStart}`;
  }
  return `Total Sheet ${cleanStart}-${cleanEnd}`;
};

const buildStatusMessage = (status) => {
  if (!status) return "";
  if (status === "queued") return "Report queued...";
  if (status === "processing") return "Processing...";
  if (status === "completed") return "Completed.";
  if (status === "failed") return "Failed.";
  return status;
};

export default function TotalSheetReport() {
  const navigate = useNavigate();
  const { currentSalon } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const helpGuide = REPORT_HELP.TOTAL_SHEET;
  const salonTimezone = useMemo(
    () => getSalonTimezone(currentSalon),
    [currentSalon]
  );
  const today = useMemo(
    () => formatDateISO(new Date(), salonTimezone),
    [salonTimezone]
  );
  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    navigate("/manager/reports", { replace: true });
  };
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState("");
  const [dateRangeError, setDateRangeError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [reportFormat, setReportFormat] = useState("pdf");
  const [reportName, setReportName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [queueLoading, setQueueLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const [actionError, setActionError] = useState("");
  const [jobStatus, setJobStatus] = useState(null);
  const [employeeSort, setEmployeeSort] = useState({
    key: "services",
    dir: "desc",
  });

  const defaultName = useMemo(
    () => buildDefaultName(startDate, endDate),
    [startDate, endDate]
  );

  useEffect(() => {
    if (!modalOpen || nameTouched) return;
    setReportName(defaultName);
  }, [defaultName, modalOpen, nameTouched]);

  useEffect(() => {
    if (isInvalidDateRange(startDate, endDate)) {
      setDateRangeError("End date can't be before start date.");
    } else {
      setDateRangeError("");
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (!jobStatus?.id) return;
    let mounted = true;
    let intervalId = null;

    const poll = async () => {
      try {
        const data = await getReportJob(jobStatus.id);
        if (!mounted) return;
        setJobStatus((prev) => ({
          ...prev,
          ...data,
          id: data.id || prev?.id,
        }));
        if (["completed", "failed"].includes(data.status)) {
          if (intervalId) clearInterval(intervalId);
        }
      } catch (error) {
        if (!mounted) return;
        setJobStatus((prev) => ({
          ...prev,
          status: "failed",
          error: { message: "Unable to refresh job status." },
        }));
        if (intervalId) clearInterval(intervalId);
      }
    };

    poll();
    intervalId = setInterval(poll, 2000);

    return () => {
      mounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [jobStatus?.id]);

  const openOptionsModal = (formatValue) => {
    if (isInvalidDateRange(startDate, endDate)) {
      setDateRangeError("End date can't be before start date.");
      return;
    }
    setReportFormat(formatValue);
    setReportName(defaultName);
    setNameTouched(false);
    setActionError("");
    setModalOpen(true);
  };

  const handleSummary = async () => {
    if (isInvalidDateRange(startDate, endDate)) {
      setDateRangeError("End date can't be before start date.");
      return;
    }
    setSummaryError("");
    setSummaryLoading(true);
    try {
      const data = await getTotalSheetSummary({
        start: startDate,
        end: endDate,
      });
      setSummary(data);
    } catch (error) {
      setSummaryError("Unable to load summary.");
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleExportNow = async () => {
    if (isInvalidDateRange(startDate, endDate)) {
      setDateRangeError("End date can't be before start date.");
      return;
    }
    setActionError("");
    setExportLoading(true);

    const isPdf = String(reportFormat).toLowerCase() === "pdf";
    let targetWindow = null;

    if (isPdf) {
      targetWindow = window.open("about:blank", "_blank");
      if (!targetWindow) {
        setActionError(
          "Pop-up blocked. Please allow pop-ups for this site to open the report."
        );
        setExportLoading(false);
        return;
      }
    }

    try {
      await exportTotalSheetNow({
        start: startDate,
        end: endDate,
        format: reportFormat,
        name: reportName,
        targetWindow,
      });
      setModalOpen(false);
    } catch (error) {
      if (targetWindow && !targetWindow.closed) {
        targetWindow.document.title = "Report Error";
        targetWindow.location.href = "about:blank";
      }
      setActionError("Unable to generate report right now.");
    } finally {
      setExportLoading(false);
    }
  };

  const handleQueueExport = async () => {
    if (isInvalidDateRange(startDate, endDate)) {
      setDateRangeError("End date can't be before start date.");
      return;
    }
    setActionError("");
    setQueueLoading(true);
    try {
      const data = await createTotalSheetJob({
        start: startDate,
        end: endDate,
        format: reportFormat,
        name: reportName,
      });
      setJobStatus({
        id: data.job_id,
        status: data.status,
        format: reportFormat,
        name: reportName,
      });
      setModalOpen(false);
    } catch (error) {
      setActionError("Unable to queue this report.");
    } finally {
      setQueueLoading(false);
    }
  };

  const handleJobDownload = async () => {
    if (!jobStatus?.id) return;
    setDownloadError("");
    setDownloadLoading(true);
    try {
      const { blob, filename } = await downloadReportJobFile(jobStatus.id);
      downloadBlob(blob, filename);
    } catch (error) {
      setDownloadError("Unable to download report. Please try again.");
    } finally {
      setDownloadLoading(false);
    }
  };

  const setRange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const setLastYear = () => {
    const year = Number(today.slice(0, 4)) - 1;
    const start = formatDateFromParts(year, 1, 1);
    const end = formatDateFromParts(year, 12, 31);
    setRange(start, end);
  };

  const setLastMonth = () => {
    const year = Number(today.slice(0, 4));
    const month = Number(today.slice(5, 7));
    let prevYear = year;
    let prevMonth = month - 1;
    if (prevMonth < 1) {
      prevMonth = 12;
      prevYear -= 1;
    }
    const endDay = new Date(Date.UTC(prevYear, prevMonth, 0)).getUTCDate();
    const start = formatDateFromParts(prevYear, prevMonth, 1);
    const end = formatDateFromParts(prevYear, prevMonth, endDay);
    setRange(start, end);
  };

  const setLastWeek = () => {
    const day = getWeekdayIndex(today);
    if (day === null) return;
    const diffToMonday = (day + 6) % 7;
    const monday = addDaysToIsoDate(today, -diffToMonday - 7);
    const sunday = addDaysToIsoDate(monday, 6);
    setRange(monday, sunday);
  };

  const setThisYear = () => {
    const year = Number(today.slice(0, 4));
    setRange(formatDateFromParts(year, 1, 1), today);
  };

  const setThisMonth = () => {
    const year = Number(today.slice(0, 4));
    const month = Number(today.slice(5, 7));
    setRange(formatDateFromParts(year, month, 1), today);
  };

  const setYesterday = () => {
    const dateValue = addDaysToIsoDate(today, -1);
    setRange(dateValue, dateValue);
  };

  const handleEmployeeSort = (key) => {
    const column = EMPLOYEE_SORT_COLUMNS[key];
    if (!column) return;
    setEmployeeSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      return { key, dir: column.type === "text" ? "asc" : "desc" };
    });
  };

  const employeesRaw = useMemo(
    () => (Array.isArray(summary?.employees) ? summary.employees : []),
    [summary?.employees]
  );

  const employeesSorted = useMemo(() => {
    if (!employeesRaw.length) return [];
    const column = EMPLOYEE_SORT_COLUMNS[employeeSort.key];
    if (!column) return [...employeesRaw];
    const rows = [...employeesRaw];
    const direction = employeeSort.dir === "asc" ? 1 : -1;
    const normalizeText = (value) => String(value ?? "").trim().toLowerCase();
    const normalizeNumber = (value) => {
      const number = Number(value);
      return Number.isFinite(number) ? number : 0;
    };
    const getValue = (row) => {
      if (employeeSort.key === "name") return normalizeText(row?.name);
      if (employeeSort.key === "tickets") return normalizeNumber(row?.tickets);
      if (employeeSort.key === "services") return normalizeNumber(row?.service);
      if (employeeSort.key === "tips") return normalizeNumber(row?.tips);
      return "";
    };
    rows.sort((a, b) => {
      const aVal = getValue(a);
      const bVal = getValue(b);
      if (column.type === "text") {
        const diff = aVal.localeCompare(bVal);
        return diff * direction;
      }
      const diff = aVal - bVal;
      if (diff !== 0) return diff * direction;
      return normalizeText(a?.name).localeCompare(normalizeText(b?.name));
    });
    return rows;
  }, [employeesRaw, employeeSort]);

  const renderPercentTag = (value) => {
    const formatted = formatPercent(value);
    if (!formatted) return null;
    return (
      <Tooltip
        text={TOTAL_SHEET_TOOLTIPS.percent_value}
        placement="top"
        maxWidth={TOOLTIP_MAX_WIDTH}
        offset={8}
        size="compact"
        allowTouch
        enableClick
      >
        <span className="ml-2 text-xs text-slate-500 cursor-help">
          ({formatted})
        </span>
      </Tooltip>
    );
  };

  const renderSortIndicator = (key) => {
    if (employeeSort.key !== key) return null;
    return (
      <span className="text-[10px] leading-none text-slate-500">
        {employeeSort.dir === "asc" ? "^" : "v"}
      </span>
    );
  };

  const renderTooltipIcon = (text, label) => (
    <InfoTooltip text={text} label={label} maxWidth={TOOLTIP_MAX_WIDTH} />
  );

  const mix = summary?.mix || {};
  const sectionCards = (summary?.sections || []).map((section) => (
    <div
      key={section.title}
      className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
    >
      <div className="flex items-center gap-2 text-sm md:text-base font-semibold text-slate-700 uppercase tracking-wide">
        <span>{section.title}</span>
        {renderTooltipIcon(
          TOTAL_SHEET_TOOLTIPS[SECTION_TOOLTIP_KEYS[section.title]],
          section.title
        )}
      </div>
      <div className="mt-3 space-y-2">
        {(section.rows || []).map((row, idx) => (
          <div key={`${section.title}-${idx}`} className="flex items-center justify-between">
            <span className="text-slate-600">
              {section.title === "Net Sales" && row.label === "Products"
                ? "Retail"
                : row.label}
            </span>
            <span className="font-semibold text-slate-900">
              {formatMoney(row.value)}
              {(() => {
                let percentValue = null;
                if (section.title === "Net Sales") {
                  if (row.label === "Services") {
                    percentValue = mix?.net_sales?.services_pct;
                  } else if (row.label === "Products" || row.label === "Retail") {
                    percentValue = mix?.net_sales?.retail_pct;
                  } else if (row.label === "Gift Cards") {
                    percentValue = mix?.net_sales?.gift_cards_pct;
                  }
                } else if (section.title === "Refunds") {
                  if (row.label === "Services") {
                    percentValue = mix?.refunds?.services_pct;
                  } else if (row.label === "Retail") {
                    percentValue = mix?.refunds?.retail_pct;
                  } else if (row.label === "Gift Cards") {
                    percentValue = mix?.refunds?.gift_cards_pct;
                  }
                } else if (section.title === "Discounts") {
                  percentValue = mix?.rates?.discount_rate;
                } else if (section.title === "Taxes") {
                  percentValue = mix?.rates?.tax_rate;
                } else if (section.title === "Payments") {
                  const key = row.key || toMixKey(row.label);
                  if (key) {
                    percentValue = mix?.payments?.[`${key}_pct`];
                  }
                }
                return renderPercentTag(percentValue);
              })()}
            </span>
          </div>
        ))}
        {section.rows?.length === 0 && (
          <div className="text-sm text-slate-500">No data.</div>
        )}
      </div>
    </div>
  ));

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4">
            <div className="text-xl md:text-2xl font-semibold text-white">
              Total Sheet
            </div>
            <div className="flex items-center gap-3">
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <div id="total-sheet-overview" className={getSectionClassName("total-sheet-overview")}>
              <ReportHelpCard
                title={helpGuide.title}
                subtitle={helpGuide.subtitle}
                sections={helpGuide.sections}
                troubleshooting={helpGuide.troubleshooting}
                storageKey="report_help_total_sheet"
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            <div
              id="total-sheet-filters"
              className={`rounded-2xl border border-slate-200 bg-slate-50 p-5 ${getSectionClassName(
                "total-sheet-filters"
              )}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="text-base md:text-lg font-semibold text-slate-900">
                  Filters
                </div>
                <button
                  type="button"
                  onClick={() => setFiltersOpen((prev) => !prev)}
                  className="text-rose-600 font-semibold hover:text-rose-700"
                >
                  [{filtersOpen ? "hide filters" : "show filters"}]
                </button>
              </div>

              {filtersOpen && (
                <div className="mt-4 space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="flex flex-col gap-1">
                      <div className="inline-flex items-center gap-2">
                        <label className={LABEL_TEXT}>Start Date</label>
                        {renderTooltipIcon(
                          TOTAL_SHEET_TOOLTIPS.start_date,
                          "Start Date"
                        )}
                      </div>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(event) => setStartDate(event.target.value)}
                        className={FIELD_CLASS}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <div className="inline-flex items-center gap-2">
                        <label className={LABEL_TEXT}>End Date</label>
                        {renderTooltipIcon(
                          TOTAL_SHEET_TOOLTIPS.end_date,
                          "End Date"
                        )}
                      </div>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(event) => setEndDate(event.target.value)}
                        className={FIELD_CLASS}
                      />
                    </div>
                  </div>
                  {dateRangeError && (
                    <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm md:text-base text-rose-700">
                      {dateRangeError}
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-3 text-sm md:text-base">
                    <div className="inline-flex items-center gap-2 text-slate-500">
                      <span>Quick pick:</span>
                      {renderTooltipIcon(
                        TOTAL_SHEET_TOOLTIPS.quick_pick,
                        "Quick pick"
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={setLastYear}
                      className="text-rose-600 font-semibold hover:text-rose-700"
                    >
                      Last Year
                    </button>
                    <button
                      type="button"
                      onClick={setLastMonth}
                      className="text-rose-600 font-semibold hover:text-rose-700"
                    >
                      Last Month
                    </button>
                    <button
                      type="button"
                      onClick={setLastWeek}
                      className="text-rose-600 font-semibold hover:text-rose-700"
                    >
                      Last Week
                    </button>
                    <button
                      type="button"
                      onClick={setThisYear}
                      className="text-rose-600 font-semibold hover:text-rose-700"
                    >
                      This Year
                    </button>
                    <button
                      type="button"
                      onClick={setThisMonth}
                      className="text-rose-600 font-semibold hover:text-rose-700"
                    >
                      This Month
                    </button>
                    <button
                      type="button"
                      onClick={setYesterday}
                      className="text-rose-600 font-semibold hover:text-rose-700"
                    >
                      Yesterday
                    </button>
                  </div>

                  <div
                    id="total-sheet-export"
                    className={`flex flex-wrap gap-3 ${getSectionClassName("total-sheet-export")}`}
                  >
                    <div className="inline-flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleSummary}
                        className={BTN_PRIMARY}
                        disabled={summaryLoading}
                      >
                        {summaryLoading ? "Loading..." : "Summary"}
                      </button>
                      {renderTooltipIcon(
                        TOTAL_SHEET_TOOLTIPS.summary_tab,
                        "Summary tab"
                      )}
                    </div>
                    <div className="inline-flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openOptionsModal("csv")}
                        className={BTN_OUTLINE}
                      >
                        Detail Export
                      </button>
                      {renderTooltipIcon(
                        TOTAL_SHEET_TOOLTIPS.detail_export_tab,
                        "Detail Export tab"
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {summaryError && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                {summaryError}
              </div>
            )}

            {summary && (
              <div className="space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-lg md:text-xl font-semibold text-slate-900">
                      Summary
                    </div>
                    <div className="text-sm md:text-base text-slate-600">
                      {summary?.meta?.start} to {summary?.meta?.end}
                    </div>
                  </div>
                  <div className="inline-flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openOptionsModal("pdf")}
                      className={BTN_OUTLINE}
                    >
                      Export to PDF
                    </button>
                    {renderTooltipIcon(
                      TOTAL_SHEET_TOOLTIPS.export_pdf,
                      "Export to PDF"
                    )}
                  </div>
                </div>

                <div
                  id="total-sheet-kpis"
                  className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-sm ${getSectionClassName(
                    "total-sheet-kpis"
                  )}`}
                >
                  <div className="grid gap-4 md:grid-cols-3 items-start">
                    <div>
                      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500">
                        <span>Total Sales &amp; Adjustments</span>
                        {renderTooltipIcon(
                          TOTAL_SHEET_TOOLTIPS.total_sales_adjustments,
                          "Total Sales & Adjustments"
                        )}
                      </div>
                      <div className="mt-2 text-lg font-semibold text-slate-900">
                        {formatMoney(summary?.totals?.total_sales_adjustments)}
                      </div>
                    </div>
                    <div className="md:text-center">
                      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500 md:justify-center">
                        <span>Total Income</span>
                        {renderTooltipIcon(
                          TOTAL_SHEET_TOOLTIPS.total_income,
                          "Total Income"
                        )}
                      </div>
                      <div className="mt-2 text-lg font-semibold text-emerald-600">
                        {formatMoney(summary?.totals?.total_income)}
                      </div>
                    </div>
                    <div className="md:text-right">
                      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500 md:justify-end">
                        <span>Total Payments</span>
                        {renderTooltipIcon(
                          TOTAL_SHEET_TOOLTIPS.total_payments,
                          "Total Payments"
                        )}
                      </div>
                      <div className="mt-2 text-lg font-semibold text-slate-900">
                        {formatMoney(summary?.totals?.total_payments)}
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  id="total-sheet-details"
                  className={`space-y-6 ${getSectionClassName("total-sheet-details")}`}
                >
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {sectionCards.length ? (
                      sectionCards
                    ) : (
                      <div className="text-sm md:text-base text-slate-500">
                        No summary sections available.
                      </div>
                    )}
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-200 text-base md:text-lg font-semibold text-slate-900">
                      <div className="inline-flex items-center gap-2">
                        <span>Employees</span>
                        {renderTooltipIcon(
                          TOTAL_SHEET_TOOLTIPS.employees,
                          "Employees"
                        )}
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead className="bg-slate-50 text-slate-600">
                          <tr>
                            <th className="px-4 py-3 text-sm md:text-base font-semibold">
                              <button
                                type="button"
                                onClick={() => handleEmployeeSort("name")}
                                className="inline-flex items-center gap-1 text-left font-semibold text-slate-600 hover:text-slate-800"
                              >
                                <span>Name</span>
                                {renderSortIndicator("name")}
                              </button>
                            </th>
                            <th className="px-4 py-3 text-sm md:text-base font-semibold">
                              <div className="inline-flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleEmployeeSort("tickets")}
                                  className="inline-flex items-center gap-1 text-left font-semibold text-slate-600 hover:text-slate-800"
                                >
                                  <span>Tickets</span>
                                  {renderSortIndicator("tickets")}
                                </button>
                                {renderTooltipIcon(
                                  TOTAL_SHEET_TOOLTIPS.employee_tickets,
                                  "Tickets"
                                )}
                              </div>
                            </th>
                            <th className="px-4 py-3 text-sm md:text-base font-semibold">
                              <div className="inline-flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleEmployeeSort("services")}
                                  className="inline-flex items-center gap-1 text-left font-semibold text-slate-600 hover:text-slate-800"
                                >
                                  <span>Services</span>
                                  {renderSortIndicator("services")}
                                </button>
                                {renderTooltipIcon(
                                  TOTAL_SHEET_TOOLTIPS.employee_services,
                                  "Services"
                                )}
                              </div>
                            </th>
                            <th className="px-4 py-3 text-sm md:text-base font-semibold">
                              <div className="inline-flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleEmployeeSort("tips")}
                                  className="inline-flex items-center gap-1 text-left font-semibold text-slate-600 hover:text-slate-800"
                                >
                                  <span>Tips</span>
                                  {renderSortIndicator("tips")}
                                </button>
                                {renderTooltipIcon(
                                  TOTAL_SHEET_TOOLTIPS.employee_tips,
                                  "Tips"
                                )}
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {employeesSorted.length === 0 ? (
                            <tr>
                              <td
                                colSpan="4"
                                className="px-4 py-4 text-sm md:text-base text-slate-500"
                              >
                                No employee activity for this range.
                              </td>
                            </tr>
                          ) : (
                            employeesSorted.map((row, idx) => (
                              <tr
                                key={`${row.name}-${idx}`}
                                className="border-t border-slate-200"
                              >
                                <td className="px-4 py-3 text-slate-700">
                                  {row.name}
                                </td>
                                <td className="px-4 py-3 text-slate-700">
                                  {row.tickets}
                                  {renderPercentTag(row.mix?.tickets_pct)}
                                </td>
                                <td className="px-4 py-3 text-slate-700">
                                  {formatMoney(row.service)}
                                  {renderPercentTag(row.mix?.services_pct)}
                                </td>
                                <td className="px-4 py-3 text-slate-700">
                                  {formatMoney(row.tips)}
                                  {renderPercentTag(row.mix?.tips_pct)}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {jobStatus?.id && (
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-base md:text-lg font-semibold text-slate-900">
                      {buildStatusMessage(jobStatus.status)}
                    </div>
                    <div className="text-sm md:text-base text-slate-600">
                      Job ID: {jobStatus.id}
                    </div>
                    {downloadError && (
                      <div className="text-sm md:text-base text-rose-600">
                        {downloadError}
                      </div>
                    )}
                    {jobStatus?.error?.message && (
                      <div className="text-sm md:text-base text-rose-600">
                        {jobStatus.error.message}
                      </div>
                    )}
                  </div>
                  {jobStatus.download_ready || jobStatus.status === "completed" ? (
                    <button
                      type="button"
                      onClick={handleJobDownload}
                      className={BTN_PRIMARY}
                      disabled={downloadLoading}
                    >
                      {downloadLoading ? "Downloading..." : "Download"}
                    </button>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <SBINModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Report Options"
        size="xl"
        fitContent
      >
        <div className="space-y-5">
          <p className="text-slate-600 text-sm md:text-base">
            Large salons or date ranges may require an extended period of time for this report
            to generate.
          </p>

          {actionError && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
              {actionError}
            </div>
          )}

          <div className="grid gap-6 md:grid-cols-[1fr_auto_1fr] items-start">
            <div className="space-y-4">
              <div className="flex flex-col gap-1">
                <label className={LABEL_TEXT}>Format</label>
                <select
                  value={reportFormat}
                  onChange={(event) => setReportFormat(event.target.value)}
                  className={FIELD_CLASS}
                >
                  <option value="pdf">PDF</option>
                  <option value="csv">CSV</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className={LABEL_TEXT}>Name</label>
                <input
                  type="text"
                  value={reportName}
                  onChange={(event) => {
                    setReportName(event.target.value);
                    setNameTouched(true);
                  }}
                  className={FIELD_CLASS}
                  placeholder={defaultName}
                />
              </div>
              <button
                type="button"
                onClick={handleQueueExport}
                className={BTN_OUTLINE}
                disabled={queueLoading}
              >
                {queueLoading ? "Queueing..." : "I can wait for it"}
              </button>
            </div>

            <div className="hidden md:block w-px bg-slate-200 h-full" />

            <div className="space-y-4">
              <div className="text-sm md:text-base text-slate-600">
                Generate instantly and open the report in a new tab.
              </div>
              <button
                type="button"
                onClick={handleExportNow}
                className={BTN_PRIMARY}
                disabled={exportLoading}
              >
                {exportLoading ? "Generating..." : "I need it now"}
              </button>
            </div>
          </div>
        </div>
      </SBINModal>
    </div>
  );
}
